import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { API } from '../config/api';
import { GamificationNotificationManager, XpToast } from '../components/GamificationNotifications';

const GamificationContext = createContext(null);
export const useGamification = () => useContext(GamificationContext);

export function GamificationProvider({ children }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifQueue, setNotifQueue] = useState([]);
  const [xpToasts, setXpToasts] = useState([]);
  const checkedIn = useRef(false);

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/gamification/profile`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Check-in on mount (daily login XP) - only once per session
  const checkIn = useCallback(async () => {
    if (!isAuthenticated || checkedIn.current) return;
    checkedIn.current = true;
    try {
      const res = await fetch(`${API}/gamification/check-in`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.xp_awarded > 0) {
          setXpToasts(prev => [...prev, { id: Date.now(), xp: data.xp_awarded, action: 'daily_login' }]);
        }
        if (data.leveled_up) {
          setNotifQueue(prev => [...prev, {
            type: 'level_up',
            level: data.level,
            rank: data.rank || 'Rookie',
            rankColor: null, // Will use default violet
          }]);
        }
        if (data.new_badges?.length) {
          data.new_badges.forEach(b => {
            setNotifQueue(prev => [...prev, { type: 'badge', badge: b }]);
          });
        }
        fetchProfile();
      }
    } catch {}
  }, [isAuthenticated, getAuthHeaders, fetchProfile]);

  useEffect(() => {
    if (isAuthenticated) {
      checkedIn.current = false;
      checkIn();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Called after user performs an XP-worthy action (post created, etc).
   * Processes the gamification result from the API response.
   */
  const processXpResult = useCallback((gamificationData) => {
    if (!gamificationData || !gamificationData.xp_awarded) return;

    // Show XP toast
    setXpToasts(prev => [...prev, {
      id: Date.now(),
      xp: gamificationData.xp_awarded,
      action: gamificationData.action || 'action',
    }]);

    if (gamificationData.leveled_up) {
      setNotifQueue(prev => [...prev, {
        type: 'level_up',
        level: gamificationData.level,
        rank: gamificationData.rank || 'Rookie',
        rankColor: gamificationData.rank_color,
      }]);
    }
    if (gamificationData.new_badges?.length) {
      gamificationData.new_badges.forEach(b => {
        setNotifQueue(prev => [...prev, { type: 'badge', badge: b }]);
      });
    }
    // Refresh profile
    fetchProfile();
  }, [fetchProfile]);

  const handleNotifProcessed = useCallback(() => {
    setNotifQueue(prev => prev.slice(1));
  }, []);

  const handleXpToastDone = useCallback((id) => {
    setXpToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <GamificationContext.Provider value={{ profile, loading, fetchProfile, processXpResult }}>
      {children}
      {/* XP floating toasts */}
      {xpToasts.map((t, i) => (
        <div key={t.id} style={{ top: `${80 + i * 52}px`, position: 'fixed', right: '16px', zIndex: 9999 }}>
          <XpToast xp={t.xp} action={t.action} onDone={() => handleXpToastDone(t.id)} />
        </div>
      ))}
      {/* Level up / Badge overlays */}
      <GamificationNotificationManager
        queue={notifQueue}
        onProcessed={handleNotifProcessed}
      />
    </GamificationContext.Provider>
  );
}
